import { ChevronRight, X } from "lucide-react";
import { useState } from "react";

const DISPLAY_STATUS = {
  Completed: "Completed",
  PendingReview: "Pending Review",
};

function toDisplayStatus(status = "") {
  const normalized = String(status).replace(/\s+/g, "").toLowerCase();
  if (normalized === "completed" || normalized.startsWith("complete(") || normalized.startsWith("completed(") || normalized === "done") return DISPLAY_STATUS.Completed;
  if (normalized === "pendingreview" || normalized === "submitted" || normalized === "inprogress") return DISPLAY_STATUS.PendingReview;
  if (normalized === "actionrequired") return "Action Required";
  if (normalized === "locked") return "Locked";
  return "Available";
}

function getStatusStyleClass(source = {}, fallbackStatus = "") {
  const colour = String(source?.statusColour || source?.StatusColour || "").trim();
  const style = String(source?.statusStyle || source?.StatusStyle || "").trim();
  const styleNo = Number(source?.statusStyleNo ?? source?.StatusStyleNo ?? 0);

  if (colour === "Green" || (!colour && (style === "Completed" || styleNo === 1))) return "bg-emerald-50 text-emerald-700 border-emerald-200";
  if (colour === "Amber" || (!colour && (style === "Pending" || styleNo === 2))) return "bg-amber-50 text-amber-700 border-amber-200";
  if (colour === "Orange" || (!colour && (style === "Available" || styleNo === 3))) return "bg-orange-50 text-orange-700 border-orange-200";
  if (colour === "Grey" || (!colour && (style === "Locked" || styleNo === 4))) return "bg-slate-100 text-slate-500 border-slate-200";
  if (colour === "Red" || (!colour && (style === "Error" || styleNo === 5))) return "bg-rose-50 text-rose-700 border-rose-200";

  const normalized = toDisplayStatus(fallbackStatus);
  if (normalized === DISPLAY_STATUS.Completed) return "bg-emerald-50 text-emerald-700 border-emerald-200";
  if (normalized === "Locked") return "bg-slate-100 text-slate-500 border-slate-200";
  if (normalized === "Action Required") return "bg-rose-50 text-rose-700 border-rose-200";
  if (normalized === DISPLAY_STATUS.PendingReview) return "bg-amber-50 text-amber-700 border-amber-200";
  return "bg-orange-50 text-orange-700 border-orange-200";
}

export default function CourseStepValidationDialog({ validation, onClose, onStepClick, courseSteps = [], isAdmin = false }) {
  const [ownershipFilter, setOwnershipFilter] = useState("");
  const message = validation?.message || validation?.Message || "This step cannot be completed yet.";
  const requestedStepName = validation?.requestedStepName || validation?.RequestedStepName || "Course step";
  const validationGroups = [
    validation?.preClassRequirements || validation?.PreClassRequirements,
    validation?.preClassLearning || validation?.PreClassLearning,
  ].filter(Boolean);
  const allCourseStepsGroup = courseSteps.length > 0 ? {
    title: "All Course Steps",
    displayStatus: `${courseSteps.length} steps`,
    items: courseSteps.map((step) => ({
      ...step,
      stepTypeNo: step.stepTypeNo ?? step.courseStepTypeNo ?? step.stepType,
      stepName: step.title || step.stepName || step.stepCode,
      statusName: step.displayStatus || step.status || step.stepStatus,
      message: step.studentStatusMessage || step.statusMessage || step.description,
    })),
  } : null;
  const groups = [...validationGroups, allCourseStepsGroup].filter(Boolean);
  const blockingGroup = validationGroups.find((group) => !(group?.isCompleted ?? group?.IsCompleted ?? false));
  const blockingStatus = blockingGroup?.displayStatus || blockingGroup?.DisplayStatus || DISPLAY_STATUS.PendingReview;
  const getOwnershipState = (entry, groupTitle) => {
    const complete = entry?.isCompleted ?? entry?.IsCompleted ?? false;
    const statusName = entry?.statusName || entry?.StatusName || entry?.displayStatus || entry?.DisplayStatus || (complete ? "Completed" : "Incomplete");
    const normalizedStatus = toDisplayStatus(entry?.displayStatus || entry?.DisplayStatus || statusName);
    const rawStatus = String(entry?.displayStatus || entry?.DisplayStatus || statusName).replace(/\s+/g, "").toLowerCase();
    const workflowStatus = String(statusName).replace(/\s+/g, "").toLowerCase();
    const reviewStatus = String(entry?.reviewStatus || entry?.ReviewStatus || entry?.reviewStatusText || entry?.ReviewStatusText || "").replace(/\s+/g, "").toLowerCase();
    const itemMessage = entry?.message || entry?.Message || "";
    const pendingStatuses = ["pendingreview", "pendingverification", "submitted", "uploaded", "awaitingreview", "pendingapproval"];
    const pendingReviewMessage = /(?:waiting|pending).*(?:admin|trainer).*(?:review|approval)/i.test(itemMessage);
    const studentManaged = groupTitle === "Pre-Class Requirements" || groupTitle === "Pre-Class Learning" || entry?.isStudentAction === true || entry?.IsStudentAction === true;
    const pendingForAdmin = isAdmin && (normalizedStatus === DISPLAY_STATUS.PendingReview || pendingStatuses.includes(rawStatus) || pendingStatuses.includes(workflowStatus) || pendingStatuses.includes(reviewStatus) || pendingReviewMessage);
    return { pendingForAdmin, pendingForStudent: isAdmin && studentManaged && !complete && !pendingForAdmin };
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-900/45 px-4 py-6">
      <div className="flex max-h-[88vh] w-full max-w-2xl flex-col overflow-hidden rounded-xl bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b border-slate-200 px-5 py-4">
          <div>
            <h2 className="text-lg font-display font-extrabold text-[#0A2540]">Step Validation</h2>
            <p className="mt-1 text-sm text-slate-500">{requestedStepName} cannot proceed until the required previous steps are complete.</p>
          </div>
          <button type="button" onClick={onClose} className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50" aria-label="Close step validation">
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="min-h-0 flex-1 space-y-4 overflow-y-auto p-5">
          <div className={`rounded-lg border px-4 py-3 text-sm font-semibold ${getStatusStyleClass(blockingGroup || {}, blockingStatus)}`}>{message}</div>
          {isAdmin && (
            <div className="flex flex-wrap gap-2" aria-label="Filter validation steps">
              <button type="button" aria-pressed={ownershipFilter === ""} onClick={() => setOwnershipFilter("")} className={`rounded-lg border px-4 py-2 text-sm font-bold transition ${ownershipFilter === "" ? "border-slate-700 bg-slate-700 text-white" : "border-slate-300 bg-white text-slate-700 hover:bg-slate-50"}`}>All Steps</button>
              <button type="button" aria-pressed={ownershipFilter === "student"} onClick={() => setOwnershipFilter((current) => current === "student" ? "" : "student")} className={`rounded-lg border px-4 py-2 text-sm font-bold transition ${ownershipFilter === "student" ? "border-blue-600 bg-blue-600 text-white" : "border-slate-300 bg-white text-slate-700 hover:bg-slate-50"}`}>Pending for Student</button>
              <button type="button" aria-pressed={ownershipFilter === "admin"} onClick={() => setOwnershipFilter((current) => current === "admin" ? "" : "admin")} className={`rounded-lg border px-4 py-2 text-sm font-bold transition ${ownershipFilter === "admin" ? "border-amber-600 bg-amber-600 text-white" : "border-slate-300 bg-white text-slate-700 hover:bg-slate-50"}`}>Pending for Admin</button>
            </div>
          )}
          {groups.map((group) => {
            const title = group?.title || group?.Title || group?.key || group?.Key || "Requirements";
            const isCompleted = group?.isCompleted ?? group?.IsCompleted ?? false;
            const groupDisplayStatus = group?.displayStatus || group?.DisplayStatus || (isCompleted ? DISPLAY_STATUS.Completed : DISPLAY_STATUS.PendingReview);
            const allItems = group?.items || group?.Items || [];
            const items = ownershipFilter
              ? allItems.filter((entry) => {
                  const ownership = getOwnershipState(entry, title);
                  return ownershipFilter === "admin" ? ownership.pendingForAdmin : ownership.pendingForStudent;
                })
              : allItems;
            if (ownershipFilter && items.length === 0) return null;
            return (
              <div key={title} className="overflow-hidden rounded-lg border border-slate-200">
                <div className="flex items-center justify-between bg-slate-50 px-4 py-3">
                  <span className="text-sm font-extrabold text-[#0A2540]">{title}</span>
                  <span className={`wc-badge border ${getStatusStyleClass(group, groupDisplayStatus)}`}>
                    {groupDisplayStatus}
                  </span>
                </div>
                {items.length > 0 && (
                  <div className="divide-y divide-slate-100">
                    {items.map((entry, index) => {
                      const name = entry?.stepName || entry?.StepName || `Requirement ${index + 1}`;
                      const complete = entry?.isCompleted ?? entry?.IsCompleted ?? false;
                      const statusName = entry?.statusName || entry?.StatusName || entry?.displayStatus || entry?.DisplayStatus || (complete ? "Completed" : "Incomplete");
                      const itemMessage = entry?.message || entry?.Message || "";
                      const stepTypeNo = Number(entry?.stepTypeNo ?? entry?.StepTypeNo ?? 0);
                      const canNavigate = stepTypeNo > 0 && typeof onStepClick === "function";
                      const isStudentManagedStep = isAdmin && (title === "Pre-Class Requirements" || title === "Pre-Class Learning" || entry?.isStudentAction === true || entry?.IsStudentAction === true);
                      const normalizedStatus = toDisplayStatus(entry?.displayStatus || entry?.DisplayStatus || statusName);
                      const pendingForAdmin = getOwnershipState(entry, title).pendingForAdmin;
                      const displayStatusName = pendingForAdmin
                        ? "Pending for Admin"
                        : isStudentManagedStep && normalizedStatus === DISPLAY_STATUS.Completed
                        ? "Completed by Student"
                          : statusName;
                      const displayMessage = pendingForAdmin
                        ? "Completed by student and waiting for admin action."
                        : isStudentManagedStep && normalizedStatus === DISPLAY_STATUS.Completed
                        ? "Completed by student."
                          : itemMessage;
                      return (
                        <button key={`${name}-${index}`} type="button" disabled={!canNavigate} onClick={() => onStepClick?.(entry)} className={`flex w-full items-start justify-between gap-4 px-4 py-3 text-left ${canNavigate ? "cursor-pointer transition hover:bg-orange-50 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-orange-300" : "cursor-default"}`}>
                          <div className="min-w-0">
                            <div className="text-sm font-semibold text-[#0A2540]">{name}</div>
                            {displayMessage && <div className="mt-1 text-xs text-slate-500">{displayMessage}</div>}
                          </div>
                          <span className="flex shrink-0 items-center gap-2">
                            <span className={`wc-badge border ${getStatusStyleClass(entry, entry?.displayStatus || entry?.DisplayStatus || statusName)}`}>{displayStatusName}</span>
                            {canNavigate && <ChevronRight className="h-4 w-4 text-slate-400" aria-hidden="true" />}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
        <div className="flex justify-end border-t border-slate-200 px-5 py-4">
          <button type="button" onClick={onClose} className="wc-btn-primary">OK</button>
        </div>
      </div>
    </div>
  );
}
